import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Table, Modal, Form, Badge, Alert } from "react-bootstrap";
import PermissionWrapper from "../components/PermissionWrapper";
import TicketManagementService from "../services/TicketManagementService";
import axiosInstance from "../services/BaseService";
import "../components/TicketManagement.css";


interface TicketOption {
  _id: string;
  ticketName: string;
  price: number;
  totalSeats?: number;
  availableSeats?: number;
  isActive: boolean;
  hasBookings?: boolean;
  isEditable?: boolean;
}

interface Schedule {
  _id: string;
  programId: {
    _id: string;
    title: string;
  };
  startDate: string;
  timing: string;
  address: string;
  organizerName: string;
}



const ManageTicketsPage: React.FC = () => {
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [ticketOptions, setTicketOptions] = useState<TicketOption[]>([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [newTicketOptions, setNewTicketOptions] = useState([{ ticketName: "", price: 0, totalSeats: 0 }]);
  const [selectedProgram, setSelectedProgram] = useState<string>("");
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [selectedState, setSelectedState] = useState<string>("");
  const [programs, setPrograms] = useState<{_id: string, title: string}[]>([]);
  const [states, setStates] = useState<{stateCode: string, name: string}[]>([]);
  const [groups, setGroups] = useState<{_id: string, name: string}[]>([]);




  useEffect(() => {
    if (selectedCountry) {
      fetchStates();
      setSelectedState("");
      setSelectedGroup("");
      setSelectedProgram("");
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (selectedState) {
      fetchGroups();
      setSelectedGroup("");
      setSelectedProgram("");
    }
  }, [selectedState]);

  useEffect(() => {
    if (selectedGroup) {
      fetchPrograms();
      setSelectedProgram("");
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (selectedProgram) {
      fetchSchedules();
    } else {
      setSchedules([]);
      setSelectedSchedule(null);
    }
  }, [selectedProgram]);

  const fetchSchedules = async () => {
    if (!selectedProgram) return;
    try {
      const response = await axiosInstance.get(`/programschedules/program/${selectedProgram}`);
      setSchedules(response.data || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await axiosInstance.get(`/states/country/${selectedCountry}`);
      setStates(response.data || []);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axiosInstance.get(`/groups?state=${selectedState}`);
      const groups = response.data.groups || response.data;
      setGroups(Array.isArray(groups) ? groups : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axiosInstance.get(`/programs/group/${selectedGroup}`);
      const programs = response.data.programs || response.data;
      setPrograms(programs || []);
    } catch (error) {
      console.error('Error fetching programs:', error);
    }
  };

  const fetchTicketOptions = async (scheduleId: string) => {
    try {
      const response = await axiosInstance.get(`/tickets/schedule/${scheduleId}`);
      setTicketOptions(response.data);
    } catch (error) {
      console.error('Error fetching ticket options:', error);
    }
  };





  const handleScheduleSelect = (schedule: Schedule) => {
    setSelectedSchedule(schedule);
    fetchTicketOptions(schedule._id);
  };

  const handleSaveTicketOptions = async () => {
    if (!selectedSchedule) return;

    try {
      await axiosInstance.post(`/tickets/schedule/${selectedSchedule._id}`, { ticketOptions: newTicketOptions });
      fetchTicketOptions(selectedSchedule._id);
      setShowTicketModal(false);
      setNewTicketOptions([{ ticketName: "", price: 0,totalSeats:0 }]);
    } catch (error: any) {
      if (error.response?.data?.lockedOptions) {
        alert(`Cannot modify ticket options with existing bookings: ${error.response.data.lockedOptions.join(', ')}`);
      } else {
        alert('Error saving ticket options');
      }
      console.error('Error saving ticket options:', error);
    }
  };



  const addTicketOption = () => {
    setNewTicketOptions([...newTicketOptions, { ticketName: "", price: 0, totalSeats: 0 }]);
  };

  const removeTicketOption = (index: number) => {
    setNewTicketOptions(newTicketOptions.filter((_, i) => i !== index));
  };

  const updateTicketOption = (index: number, field: string, value: any) => {
    const updated = [...newTicketOptions];
    updated[index] = { ...updated[index], [field]: value };
    setNewTicketOptions(updated);
  };



  return (
    <PermissionWrapper pageName="ManageAllTicketsPage">
      <Container fluid>
        <Row className="mb-4">
          <Col>
            <h2>
              <i className="bi bi-ticket-perforated me-2"></i>
              Ticket Management System
            </h2>
          </Col>
        </Row>



        {/* Filters */}
        <Row className="mb-4 g-3">
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <Form.Label>Country</Form.Label>
              <Form.Select value={selectedCountry} onChange={(e) => setSelectedCountry(e.target.value)}>
                <option value="">Choose Country...</option>
                <option value="IN">India</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <Form.Label>State</Form.Label>
              <Form.Select value={selectedState} onChange={(e) => setSelectedState(e.target.value)} disabled={!selectedCountry}>
                <option value="">Choose State...</option>
                {states.map(state => (
                  <option key={state.stateCode} value={state.stateCode}>{state.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <Form.Label>Group</Form.Label>
              <Form.Select value={selectedGroup} onChange={(e) => setSelectedGroup(e.target.value)} disabled={!selectedState}>
                <option value="">Choose Group...</option>
                {groups.map(group => (
                  <option key={group._id} value={group._id}>{group.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} sm={6} md={3}>
            <Form.Group>
              <Form.Label>Program/Play</Form.Label>
              <Form.Select value={selectedProgram} onChange={(e) => setSelectedProgram(e.target.value)} disabled={!selectedGroup}>
                <option value="">Choose Program...</option>
                {programs.map(program => (
                  <option key={program._id} value={program._id}>{program.title}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {!selectedProgram && (
          <Row className="mb-4">
            <Col>
              <Alert variant="info" className="text-center">
                <i className="bi bi-info-circle me-2"></i>
                Please select Country → State → Group → Program to manage tickets.
              </Alert>
            </Col>
          </Row>
        )}

        {selectedProgram && (
          <Row className="g-3">
            {/* Schedule Selection */}
            <Col xs={12} lg={4}>
              <Card>
                <Card.Header>
                  <h5><i className="bi bi-calendar2-week me-2"></i>Schedules</h5>
                </Card.Header>
                <Card.Body className="schedule-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {schedules.length > 0 ? schedules.map((schedule) => (
                    <Card 
                      key={schedule._id} 
                      className={`mb-2 schedule-card ${selectedSchedule?._id === schedule._id ? 'selected' : ''}`}
                      onClick={() => handleScheduleSelect(schedule)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Card.Body className="p-3">
                        <h6 className="mb-1">{schedule.programId?.title}</h6>
                        <small className="text-muted d-block">
                          <i className="bi bi-calendar me-1"></i>
                          {new Date(schedule.startDate).toLocaleDateString()}
                        </small>
                        <small className="text-muted d-block">
                          <i className="bi bi-clock me-1"></i>
                          {schedule.timing}
                        </small>
                        <small className="text-muted d-block">
                          <i className="bi bi-geo-alt me-1"></i>
                          {schedule.address}
                        </small>
                      </Card.Body>
                    </Card>
                  )) : (
                    <div className="text-center p-3">
                      <i className="bi bi-calendar-x display-4 text-muted"></i>
                      <p className="text-muted mt-2">No schedules found for this program.</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Ticket Management */}
            <Col xs={12} lg={8}>
            {selectedSchedule ? (
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5><i className="bi bi-ticket me-2"></i>Ticket Options</h5>
                  <Button 
                    variant={ticketOptions.some(t => t.hasBookings) ? "warning" : "primary"}
                    size="sm"
                    onClick={() => {
                      setNewTicketOptions(ticketOptions.length > 0 ? 
                        ticketOptions.map(t => ({ ticketName: t.ticketName, price: t.price, totalSeats: t.totalSeats || 0 })) :
                        [{ ticketName: "", price: 0, totalSeats: 0 }]
                      );
                      setShowTicketModal(true);
                    }}
                    disabled={ticketOptions.some(t => t.hasBookings)}
                    title={ticketOptions.some(t => t.hasBookings) ? "Some tickets have bookings - editing restricted" : "Manage ticket options"}
                  >
                    {ticketOptions.some(t => t.hasBookings) ? (
                      <><i className="bi bi-lock me-1"></i>Locked</>
                    ) : (
                      <><i className="bi bi-plus me-1"></i>Manage Tickets</>
                    )}
                  </Button>
                </Card.Header>
                <Card.Body>
                  {ticketOptions.length > 0 ? (
                    <>
                      {/* Desktop Table */}
                      <div className="d-none d-md-block">
                        <Table striped hover>
                          <thead>
                            <tr>
                              <th>Ticket Type</th>
                              <th>Price</th>
                              <th>Available Seats</th>
                              <th>Total Seats</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ticketOptions.map((ticket) => (
                              <tr key={ticket._id} className={ticket.hasBookings ? 'table-warning' : ''}>
                                <td>
                                  {ticket.ticketName}
                                  {ticket.hasBookings && (
                                    <small className="d-block text-warning">
                                      <i className="bi bi-lock-fill me-1"></i>
                                      Has bookings
                                    </small>
                                  )}
                                </td>
                                <td>₹{ticket.price}</td>
                                <td>
                                  <Badge bg={ticket.availableSeats === 0 ? "danger" : ticket.availableSeats! < 10 ? "warning" : "success"}>
                                    {ticket.availableSeats || 0}
                                  </Badge>
                                </td>
                                <td>{ticket.totalSeats || 0}</td>
                                <td>
                                  <Badge bg={ticket.isActive ? "success" : "secondary"}>
                                    {ticket.isActive ? "Active" : "Inactive"}
                                  </Badge>
                                  {ticket.hasBookings && (
                                    <Badge bg="warning" className="ms-1">
                                      <i className="bi bi-lock-fill"></i>
                                    </Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      
                      {/* Mobile Cards */}
                      <div className="d-md-none">
                        <Row className="g-3">
                          {ticketOptions.map((ticket) => (
                            <Col xs={12} key={ticket._id}>
                              <Card className={`border-0 shadow-sm ${ticket.hasBookings ? 'border-warning' : ''}`} 
                                    style={{ backgroundColor: ticket.hasBookings ? 'rgba(255, 193, 7, 0.1)' : 'white' }}>
                                <Card.Body className="p-3">
                                  <div className="d-flex justify-content-between align-items-start mb-2">
                                    <div>
                                      <h6 className="mb-0 text-primary">{ticket.ticketName}</h6>
                                      {ticket.hasBookings && (
                                        <small className="text-warning">
                                          <i className="bi bi-lock-fill me-1"></i>
                                          Booking started - No changes allowed
                                        </small>
                                      )}
                                    </div>
                                    <div className="d-flex gap-1">
                                      <Badge bg={ticket.isActive ? "success" : "secondary"}>
                                        {ticket.isActive ? "Active" : "Inactive"}
                                      </Badge>
                                      {ticket.hasBookings && (
                                        <Badge bg="warning">
                                          <i className="bi bi-lock-fill"></i>
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                  <div className="row g-2">
                                    <div className="col-6">
                                      <small className="text-muted d-block">Price</small>
                                      <strong>₹{ticket.price}</strong>
                                    </div>
                                    <div className="col-6">
                                      <small className="text-muted d-block">Available</small>
                                      <Badge bg={ticket.availableSeats === 0 ? "danger" : ticket.availableSeats! < 10 ? "warning" : "success"}>
                                        {ticket.availableSeats || 0} / {ticket.totalSeats || 0}
                                      </Badge>
                                    </div>
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    </>
                  ) : (
                    <Alert variant="info">
                      <i className="bi bi-info-circle me-2"></i>
                      No ticket options configured for this schedule.
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            ) : (
              <Card>
                <Card.Body className="text-center py-5">
                  <i className="bi bi-arrow-left display-1 text-muted d-none d-lg-block"></i>
                  <i className="bi bi-arrow-up display-1 text-muted d-lg-none"></i>
                  <h5 className="mt-3 text-muted">Select a schedule to manage tickets</h5>
                </Card.Body>
              </Card>
            )}
            </Col>
          </Row>
        )}

        {/* Ticket Options Modal */}
        <Modal show={showTicketModal} onHide={() => setShowTicketModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Manage Ticket Options</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              {newTicketOptions.map((option, index) => (
                <Row key={index} className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Ticket Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="e.g., General, VIP, Student"
                        value={option.ticketName}
                        onChange={(e) => updateTicketOption(index, 'ticketName', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Price (₹)</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="0"
                        value={option.price}
                        onChange={(e) => updateTicketOption(index, 'price', parseFloat(e.target.value) || 0)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Total Seats</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="0"
                        min="1"
                        value={option.totalSeats}
                        onChange={(e) => updateTicketOption(index, 'totalSeats', parseInt(e.target.value) || 0)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button
                      variant="outline-danger"
                      onClick={() => removeTicketOption(index)}
                      disabled={newTicketOptions.length === 1}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button variant="outline-primary" onClick={addTicketOption}>
                <i className="bi bi-plus me-1"></i>Add Ticket Option
              </Button>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTicketModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSaveTicketOptions}
              disabled={ticketOptions.some(t => t.hasBookings)}
              title={ticketOptions.some(t => t.hasBookings) ? "Cannot save - some options have bookings" : "Save ticket options"}
            >
              {ticketOptions.some(t => t.hasBookings) ? "Locked" : "Save Ticket Options"}
            </Button>
          </Modal.Footer>
        </Modal>


      </Container>
    </PermissionWrapper>
  );
};

export default ManageTicketsPage;